if (!customElements.get('product-zoom-popup')) {
  customElements.define(
    'product-zoom-popup',
    class ProductZoomPopup extends HTMLElement {
      constructor() {
        super();
        this.zoomScale = 2.5;
        this.maxScale = 4;
        this.dragThreshold = 6;
        this.swipeThreshold = 50;
        this.isSwiping = false;
        this.scale = 1;
        this.translateX = 0;
        this.translateY = 0;
        this.pointers = new Map();
        this.isPinching = false;
        this.pinchHappened = false;
        this.onTriggerClick = this.onTriggerClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
      }

      connectedCallback() {
        if (this.moved) return;
        this.moved = true;

        this.sectionId = this.dataset.sectionId;
        this.stage = this.querySelector('[data-zoom-stage]');
        this.track = this.querySelector('[data-zoom-track]');
        this.slides = Array.from(this.querySelectorAll('.zoom-popup__slide'));
        this.thumbs = Array.from(this.querySelectorAll('[data-zoom-thumb]'));
        this.prevButton = this.querySelector('[data-zoom-prev]');
        this.nextButton = this.querySelector('[data-zoom-next]');
        this.visibleSlides = this.slides.slice();

        const gallery = document.getElementById(`MediaGallery-${this.sectionId}`);
        if (gallery) gallery.addEventListener('click', this.onTriggerClick, true);

        this.slides.forEach((slide) => {
          const image = slide.querySelector('.zoom-popup__image');
          if (image) image.addEventListener('pointerdown', this.onPointerDown);
        });

        this.querySelectorAll('[data-zoom-close]').forEach((el) =>
          el.addEventListener('click', () => this.close())
        );

        if (this.prevButton) this.prevButton.addEventListener('click', () => this.step(-1));
        if (this.nextButton) this.nextButton.addEventListener('click', () => this.step(1));
        this.thumbs.forEach((thumb) =>
          thumb.addEventListener('click', () => this.goTo(thumb.dataset.zoomThumb))
        );

        // Move to body so it is never clipped by ancestor overflow/transform.
        document.body.appendChild(this);
      }

      onTriggerClick(event) {
        const toggle = event.target.closest('.product__media-zoom-custom_popup');
        if (!toggle) return;

        const container = toggle.closest('.product-media-container');
        // Let videos/3D models fall through to the built-in media modal.
        if (container && !container.classList.contains('media-type-image')) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.open(toggle.getAttribute('data-media-id'));
      }

      open(mediaId) {
        this.applyColorFilter();
        const isVisible = this.visibleSlides.some((s) => s.dataset.mediaId === String(mediaId));
        this.goTo(isVisible ? mediaId : this.visibleSlides[0]?.dataset.mediaId, true);
        this.setAttribute('open', '');
        document.body.classList.add('zoom-popup-open');
        document.addEventListener('keydown', this.onKeyDown);
        const closeButton = this.querySelector('[data-zoom-close]');
        if (closeButton) closeButton.focus();
      }

      close() {
        this.resetZoom();
        this.removeAttribute('open');
        document.body.classList.remove('zoom-popup-open');
        document.removeEventListener('keydown', this.onKeyDown);
        const opener = document.querySelector(
          `#MediaGallery-${this.sectionId} .product__media-zoom-custom_popup[data-media-id="${this.currentId}"]`
        );
        if (opener) opener.focus();
      }

      onKeyDown(event) {
        switch (event.code.toUpperCase()) {
          case 'ESCAPE':
            this.close();
            break;
          case 'ARROWLEFT':
            this.step(-1);
            break;
          case 'ARROWRIGHT':
            this.step(1);
            break;
        }
      }

      goTo(mediaId, isOpening = false) {
        let slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
        if (slide && slide.hidden) slide = null;
        if (!slide) {
          // Fall back to the first slide visible for the current colour.
          const fallback = this.visibleSlides[0];
          if (fallback && fallback.dataset.mediaId !== String(mediaId)) {
            this.goTo(fallback.dataset.mediaId, isOpening);
          }
          return;
        }

        this.resetZoom();
        this.currentId = slide.dataset.mediaId;

        this.thumbs.forEach((t) =>
          t.classList.toggle('is-active', t.dataset.zoomThumb === this.currentId)
        );

        this.activeSlide = slide;
        this.activeImage = slide.querySelector('.zoom-popup__image');
        this.loadHiRes();
        this.updateNavState();
        this.positionTrack(!isOpening);

        const activeThumb = this.thumbs.find((t) => t.dataset.zoomThumb === this.currentId);
        if (activeThumb) activeThumb.scrollIntoView({ block: 'nearest', inline: 'center' });
      }

      positionTrack(animate = true) {
        if (!this.track) return;
        const index = this.currentIndex();
        if (index < 0) return;
        if (!animate) this.track.style.transition = 'none';
        this.track.style.transform = `translateX(-${index * 100}%)`;
        if (!animate) {
          // Force reflow so the next move animates from this position.
          void this.track.offsetWidth;
          this.track.style.transition = '';
        }
      }

      currentIndex() {
        return this.visibleSlides.findIndex((s) => s.dataset.mediaId === this.currentId);
      }

      step(direction) {
        const next = this.currentIndex() + direction;
        if (next < 0 || next >= this.visibleSlides.length) return;
        this.goTo(this.visibleSlides[next].dataset.mediaId);
      }

      updateNavState() {
        const count = this.visibleSlides.length;
        const single = count <= 1;
        const index = this.currentIndex();
        if (this.prevButton) {
          this.prevButton.hidden = single;
          this.prevButton.disabled = index <= 0;
        }
        if (this.nextButton) {
          this.nextButton.hidden = single;
          this.nextButton.disabled = index >= count - 1;
        }
      }

      normalize(value) {
        return (value || '').toLowerCase().replace(/\s+/g, ' ').trim();
      }

      getSelectedColor() {
        const scope = document.getElementById(`MainProduct-${this.sectionId}`) || document;
        const checked = scope.querySelector('.js-color-option:checked');
        if (checked) return checked.value;
        const variantSelects = scope.querySelector('variant-selects');
        return variantSelects?.currentVariant?.option1 || '';
      }

      applyColorFilter() {
        const color = this.normalize(this.getSelectedColor());
        let matched = [];
        if (color) {
          matched = this.slides.filter((s) => this.normalize(s.dataset.color) === color);
        }
        // Fall back to all images if the colour has no dedicated media.
        if (!matched.length) matched = this.slides.slice();

        this.visibleSlides = matched;
        const visibleIds = new Set(matched.map((s) => s.dataset.mediaId));
        this.slides.forEach((s) => {
          s.hidden = !visibleIds.has(s.dataset.mediaId);
        });
        this.thumbs.forEach((t) => {
          t.hidden = !visibleIds.has(t.dataset.zoomThumb);
        });
      }

      loadHiRes() {
        const image = this.activeImage;
        if (!image) return;
        // Upgrade to the high-resolution source on first view.
        const hiRes = image.dataset.zoomSrc;
        if (hiRes && image.dataset.hiResLoaded !== 'true') {
          image.src = hiRes;
          image.removeAttribute('srcset');
          image.dataset.hiResLoaded = 'true';
        }
      }

      onPointerDown(event) {
        if (!this.activeImage) return;
        event.preventDefault();
        this.activeImage.setPointerCapture(event.pointerId);
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        this.activeImage.addEventListener('pointermove', this.onPointerMove);
        this.activeImage.addEventListener('pointerup', this.onPointerUp);
        this.activeImage.addEventListener('pointercancel', this.onPointerUp);

        if (this.pointers.size >= 2) {
          this.startPinch();
        } else {
          // Single pointer: prepare for tap-to-zoom or drag-to-pan.
          this.pointer = {
            startX: event.clientX,
            startY: event.clientY,
            originX: this.translateX,
            originY: this.translateY,
            moved: false,
          };
        }
      }

      onPointerMove(event) {
        if (this.pointers.has(event.pointerId)) {
          this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        }

        if (this.isPinching && this.pointers.size >= 2) {
          this.handlePinch();
          return;
        }

        if (!this.pointer) return;
        const dx = event.clientX - this.pointer.startX;
        const dy = event.clientY - this.pointer.startY;
        if (!this.pointer.moved && Math.hypot(dx, dy) > this.dragThreshold) {
          this.pointer.moved = true;
        }
        if (!this.pointer.moved) return;

        if (this.isZoomed) {
          this.activeSlide.classList.add('is-panning');
          this.setTransform(this.pointer.originX + dx, this.pointer.originY + dy);
        } else {
          // Not zoomed: drag the track to swipe between images.
          this.dragTrack(dx);
        }
      }

      onPointerUp(event) {
        this.pointers.delete(event.pointerId);

        if (this.pointers.size === 1 && this.isPinching) {
          // One finger lifted mid-pinch: keep zoom, continue panning with the other.
          this.isPinching = false;
          const remaining = [...this.pointers.values()][0];
          this.pointer = {
            startX: remaining.x,
            startY: remaining.y,
            originX: this.translateX,
            originY: this.translateY,
            moved: true,
          };
          return;
        }

        if (this.pointers.size > 0) return;

        this.activeImage.removeEventListener('pointermove', this.onPointerMove);
        this.activeImage.removeEventListener('pointerup', this.onPointerUp);
        this.activeImage.removeEventListener('pointercancel', this.onPointerUp);
        this.activeSlide.classList.remove('is-panning');

        if (this.isSwiping) {
          this.endSwipe(event);
        } else if (this.pointer && !this.pointer.moved && !this.pinchHappened) {
          // A clean tap: toggle zoom centered on the pointer.
          this.toggleZoom(event.clientX, event.clientY);
        } else if (this.scale <= 1.01) {
          this.resetZoom();
        }

        this.pointer = null;
        this.isPinching = false;
        this.pinchHappened = false;
        this.isSwiping = false;
      }

      dragTrack(dx) {
        if (!this.track || this.visibleSlides.length <= 1) return;
        this.isSwiping = true;
        this.track.style.transition = 'none';
        const basePx = -this.currentIndex() * this.stage.clientWidth;
        const index = this.currentIndex();
        const atStart = index === 0 && dx > 0;
        const atEnd = index === this.visibleSlides.length - 1 && dx < 0;
        // Add resistance when dragging past the first/last image.
        const offset = atStart || atEnd ? dx * 0.3 : dx;
        this.track.style.transform = `translateX(${basePx + offset}px)`;
      }

      endSwipe(event) {
        this.track.style.transition = '';
        const dx = event.clientX - this.pointer.startX;
        const direction = dx < 0 ? 1 : -1;
        const target = this.currentIndex() + direction;
        if (Math.abs(dx) > this.swipeThreshold && target >= 0 && target < this.visibleSlides.length) {
          this.step(direction);
        } else {
          this.positionTrack(true);
        }
      }

      startPinch() {
        if (this.isSwiping) {
          // Abort an in-progress swipe and snap the track back before pinching.
          this.isSwiping = false;
          this.track.style.transition = '';
          this.positionTrack(false);
        }
        const pts = [...this.pointers.values()];
        this.pinchStartDist = this.distance(pts[0], pts[1]) || 1;
        this.pinchStartScale = this.scale;
        this.lastFocal = this.focalPoint(pts[0], pts[1]);
        this.isPinching = true;
        this.pinchHappened = true;
        this.pointer = null;
        this.isZoomed = true;
        this.activeSlide.classList.add('is-zoomed', 'is-panning');
        this.classList.add('is-zoomed');
      }

      handlePinch() {
        const pts = [...this.pointers.values()];
        const dist = this.distance(pts[0], pts[1]);
        const focal = this.focalPoint(pts[0], pts[1]);
        let newScale = (this.pinchStartScale * dist) / this.pinchStartDist;
        newScale = Math.max(1, Math.min(this.maxScale, newScale));

        // Zoom about the pinch midpoint, then pan by how that midpoint moved.
        const ratio = newScale / this.scale;
        let tx = focal.x * (1 - ratio) + ratio * this.translateX;
        let ty = focal.y * (1 - ratio) + ratio * this.translateY;
        tx += focal.x - this.lastFocal.x;
        ty += focal.y - this.lastFocal.y;

        this.scale = newScale;
        this.lastFocal = focal;
        this.isZoomed = newScale > 1.01;
        this.setTransform(tx, ty);
      }

      distance(a, b) {
        return Math.hypot(a.x - b.x, a.y - b.y);
      }

      focalPoint(a, b) {
        const stageRect = this.stage.getBoundingClientRect();
        return {
          x: (a.x + b.x) / 2 - (stageRect.left + stageRect.width / 2),
          y: (a.y + b.y) / 2 - (stageRect.top + stageRect.height / 2),
        };
      }

      toggleZoom(clientX, clientY) {
        if (this.isZoomed) {
          this.resetZoom();
          return;
        }
        this.isZoomed = true;
        this.scale = this.zoomScale;
        this.activeSlide.classList.add('is-zoomed');
        this.classList.add('is-zoomed');

        const stageRect = this.stage.getBoundingClientRect();
        const offsetX = clientX - (stageRect.left + stageRect.width / 2);
        const offsetY = clientY - (stageRect.top + stageRect.height / 2);
        this.setTransform(-offsetX * this.scale, -offsetY * this.scale);
      }

      resetZoom() {
        this.isZoomed = false;
        this.scale = 1;
        this.translateX = 0;
        this.translateY = 0;
        if (this.activeSlide) this.activeSlide.classList.remove('is-zoomed', 'is-panning');
        this.classList.remove('is-zoomed');
        if (this.activeImage) this.activeImage.style.transform = 'translate3d(0, 0, 0) scale(1)';
      }

      setTransform(x, y) {
        const bounds = this.panBounds();
        this.translateX = Math.max(-bounds.x, Math.min(bounds.x, x));
        this.translateY = Math.max(-bounds.y, Math.min(bounds.y, y));
        this.activeImage.style.transform = `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.scale})`;
      }

      panBounds() {
        const stageRect = this.stage.getBoundingClientRect();
        const width = this.activeImage.offsetWidth * this.scale;
        const height = this.activeImage.offsetHeight * this.scale;
        return {
          x: Math.max(0, (width - stageRect.width) / 2),
          y: Math.max(0, (height - stageRect.height) / 2),
        };
      }
    }
  );
}
